/**
 * A viewing room is a page a collector publishes at `/room/<handle>/<slug>`.
 *
 * The vocabulary lives here rather than in either app because both sides need
 * it and neither owns it: the API validates a scope with `z.enum(VIEWING_ROOM_SCOPES)`
 * and the client renders a picker off the same array, so the two cannot disagree
 * about what a room may be.
 */
/**
 * What a room draws its works from.
 *
 * `collections` and `works` resolve differently, and the difference is the
 * product decision rather than an implementation detail: a named collection is a
 * LIVE membership — a work registered into it tomorrow appears in the room
 * tomorrow — while a named work is a fixed list. `ondo.viewing_room_collections`
 * and `ondo.viewing_room_works` are two tables for exactly that reason.
 *
 * These values must stay in step with the `ondo.viewing_room_scope` enum in
 * supabase/migrations.
 */
export const VIEWING_ROOM_SCOPES = ["all_collections", "collections", "works"];
/**
 * Where a room is in its life.
 *
 * `hidden` answers at its URL exactly as a room that never existed does, and
 * `deleted` is a status rather than a DELETE because the visitor log has to
 * outlive the room — an audit log its own subject can erase is not one. A
 * deleted room's slug is never reissued and it cannot be reopened.
 *
 * Must stay in step with `ondo.viewing_room_status`.
 */
export const VIEWING_ROOM_STATUSES = ["open", "hidden", "deleted"];
/**
 * The collector's public address, used in room URLs and nowhere else.
 *
 * Three to thirty characters, lowercase, no leading or trailing hyphen — the
 * same expression the check constraint on `ondo.principals.handle` carries, so
 * a handle the client accepts is one the database will.
 *
 * Deliberately not the ONDO id. Putting `ONDO-1001` in a public URL would tie
 * every room to the id space `share_collection` goes out of its way not to leak.
 */
export const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/;
/** Twenty-two hex characters off a v4 uuid — about 85 bits. See `ondo.new_room_slug`. */
export const ROOM_SLUG_PATTERN = /^[a-z0-9]{22}$/;
export function isViewingRoomScope(value) {
    return (typeof value === "string" &&
        VIEWING_ROOM_SCOPES.includes(value));
}
/**
 * Lowercases and trims, because a collector typing their own name will
 * capitalise it and the database stores one case.
 *
 * Normalising rather than rejecting is the right shape here and the wrong one
 * for the rules that follow it: 'Hue-Williams' is unambiguously the handle
 * 'hue-williams', while 'hue williams' is not unambiguously anything.
 */
export function normaliseHandle(value) {
    return value.trim().toLowerCase();
}
export function isHandle(value) {
    return typeof value === "string" && HANDLE_PATTERN.test(value);
}
/**
 * The public address of a room.
 *
 * One function so the client, the API and any link the API ever emails cannot
 * spell it three ways. It builds a path and not a URL: the origin belongs to
 * whoever is rendering, and a shared package guessing at one is how a staging
 * deployment sends production links.
 */
export function roomPath(handle, slug) {
    return `/room/${encodeURIComponent(handle)}/${encodeURIComponent(slug)}`;
}
//# sourceMappingURL=viewingRoom.js.map